import { useEffect, useRef } from 'react'

import { ConversationSummaryDTO } from 'models/conversations'
import { getConversationDisplayTitle } from './conversationDisplay'
import { lastMessagePreviewText } from './chatAttachments'
import { useBrowserNotifications } from './useBrowserNotifications'
import { useChatWidget } from '../ChatWidget.context'
import { useResolvedCurrentUserId } from './useResolvedCurrentUserId'
import { useTranslation } from 'react-i18next'

/**
 * Raises a desktop notification whenever a conversation's unread count goes UP — driven off the
 * conversation-LIST data rather than the socket message stream.
 *
 * Why list-driven: a brand-new conversation's very first message can be missed by the socket
 * (it's broadcast on `/topic/conversations/{id}` during the round-trip it takes our SUBSCRIBE frame
 * to register at the broker — STOMP topics don't replay). The list, refetched on the
 * conversation-created event and on every poll, ALWAYS reflects that first message (server-side
 * `unreadCount` + `lastMessagePreview`), so notifying off unread increases is race-free.
 *
 * Every conversation notifies — including the selected one — except the thread that is actually on
 * screen (widget open on it AND tab visible, see `onScreenConversationId`).
 *
 * @param conversations current conversation summaries (from `useGetConversations`)
 * @param ready whether the conversation list has settled at least once — gates the baseline so we
 *   never notify for unread history that already existed when the widget mounted.
 */
export const useUnreadNotifications = (conversations: ConversationSummaryDTO[], ready: boolean) => {
  const { t } = useTranslation(['chat'])
  const notify = useBrowserNotifications()
  const currentUserId = useResolvedCurrentUserId()
  const { onScreenConversationId } = useChatWidget()

  // Per-conversation unread baseline; seeded on the first settled load so pre-existing unread never
  // fires, only increases observed afterwards.
  const seenUnreadRef = useRef<Map<string, number>>(new Map())
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!ready) return

    const next = new Map<string, number>()
    for (const conversation of conversations) {
      next.set(conversation.id, conversation.unreadCount ?? 0)
    }

    // First settled snapshot: record the baseline, don't notify for anything already there.
    if (!initializedRef.current) {
      initializedRef.current = true
      seenUnreadRef.current = next
      return
    }

    const prev = seenUnreadRef.current
    for (const conversation of conversations) {
      const count = conversation.unreadCount ?? 0
      const before = prev.get(conversation.id) ?? 0
      // Notify on a net increase, unless that thread is on screen (widget open on it and tab visible).
      if (count > before && conversation.id !== onScreenConversationId) {
        notify({
          title: getConversationDisplayTitle(conversation, currentUserId, t),
          body: lastMessagePreviewText(conversation, t) || t('chat:notification.message_title'),
          tag: `conversation-${conversation.id}`,
        })
      }
    }

    seenUnreadRef.current = next
  }, [conversations, ready, onScreenConversationId, currentUserId, notify, t])
}
